import Edit16 from '@carbon/icons-react/es/edit/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Tooltip } from 'antd';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { IdpType } from 'api/account';
import { SamlConfig } from 'api/security/idp';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { Loading } from 'components/atoms/loading';
import { confirm } from 'components/atoms/modal';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { Switch } from 'components/atoms/switch/switch';
import { toastService } from 'components/molecules/toast';
import { BodyLong01 } from 'components/styles';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import {
  useQueryIdps,
  useSSOCreation,
  useSamlDelete,
  useSSOUpdate,
} from 'queries/saml';
import { SubTitle } from './component';
import { SamlFormPage, SetupSamlDrawer } from './setup-saml-drawer';

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  justify-items: center;
  gap: var(--spacing-04);
`;

const Wrapper = styled.div`
  display: flex;
  gap: var(--spacing-03);
  .ant-dropdown-trigger {
    align-self: center;
  }
`;

const StyledLink = styled(SubTitle)`
  color: var(--blue-60) !important;
  cursor: pointer;
`;

const IdpWrapper = styled.div`
  flex-grow: 1;
`;

const SpaceDiv = styled.div`
  width: calc(var(--spacing-04) + var(--spacing-05));
`;
const LoaderWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SetupBody = styled.p`
  ${BodyLong01};
  margin-bottom: 0;
  color: var(--gray-70);
`;

const SwitchWrapper = styled.div``;

export const CategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: var(--spacing-05);
  gap: var(--spacing-01);
`;

export const SSOList: FC = () => {
  const { data: idps, isLoading } = useQueryIdps();
  const { mutate: createSaml, isLoading: isSaving } = useSSOCreation();

  const { mutateAsync } = useSSOUpdate();
  const { mutateAsync: deleteIdp } = useSamlDelete();
  const [editInfo, setEditInfo] = useState<{
    idpId?: string;
    enableEdit: boolean;
    page?: SamlFormPage;
  }>({
    idpId: undefined,
    enableEdit: false,
    page: undefined,
  });
  const translate = useTranslate();

  const onDisableEdit = () => {
    setEditInfo({
      enableEdit: false,
      idpId: undefined,
      page: undefined,
    });
  };
  const onEnableEdit = (idpId) => {
    setEditInfo({
      enableEdit: true,
      idpId,
      page: SamlFormPage.SETUP,
    });
  };
  const showMetaPage = (idpId) => {
    setEditInfo({
      enableEdit: true,
      idpId,
      page: SamlFormPage.META,
    });
  };
  const deleteSamlConfig = (idpId) =>
    deleteIdp(idpId, {
      onSuccess: () =>
        toastService({
          title: translate('config.deleted'),
          message: translate('settings.saml.deleted.body'),
        }).success(),
    });

  const onDelete = (idpId) => {
    confirm({
      title: translate('settings.saml.delete.title'),
      content: translate('settings.saml.delete.body', {
        strong: (chunk) => <strong>{chunk}</strong>,
      }),
      okText: translate('delete'),
      cancelText: translate('cancel'),
      okType: 'danger',
      onOk: async () => deleteSamlConfig(idpId),
    });
  };

  const googleSSO = idps?.find((item) => item.idpType === IdpType.Google);
  const samlSSO = idps?.find((item) => item.idpType === IdpType.Saml);

  const showToast = (enabled) => {
    const prefix = 'setup_profile.sso';
    const suffix = enabled ? 'enabled' : 'disabled';

    toastService({
      title: translate(`${prefix}.title.${suffix}` as TranslationKey),
      message: translate(`${prefix}.message.${suffix}` as TranslationKey),
    }).success();
  };

  const enableIdp = (idp: SamlConfig, enabled: boolean) => {
    return mutateAsync(
      { enabled, idpId: idp.idpId, idpType: idp.idpType },
      {
        onSuccess: () => showToast(enabled),
      },
    );
  };

  const onChange = (idp: SamlConfig, enabled: boolean) => {
    if (enabled) {
      confirm({
        title: translate('settings.security.saml.model.title'),
        content: translate('settings.security.saml.model.body'),
        okText: translate('settings.security.saml.model.ok'),
        cancelText: translate('settings.security.saml.model.cancel'),
        onOk: () => enableIdp(idp, enabled),
        closable: true,
      });

      return;
    }
    confirm({
      title: translate('setup_profile.sso.disabled.confirm.title'),
      content: translate('setup_profile.sso.disabled.confirm.body', {
        strong: (chunk) => <strong>{chunk}</strong>,
      }),
      okText: translate('disable'),
      cancelText: translate('cancel'),
      onOk: async () => enableIdp(idp, enabled),
      okType: 'danger',
    });
  };

  const showTitle = idps?.find((item) => item.enabled);

  return (
    <CategoryWrapper>
      {showTitle && (
        <SubTitle>
          <FormattedMessage id="setings.sso.title" />
        </SubTitle>
      )}
      <ListWrapper>
        <Wrapper>
          {isSaving ? (
            <LoaderWrapper>
              <Loading size="default" />
            </LoaderWrapper>
          ) : (
            <SwitchWrapper>
              <Tooltip
                title={translate(
                  samlSSO?.enabled
                    ? 'setup_profile.sso.tooltip.only.one.sso'
                    : googleSSO?.enabled
                    ? 'setup_profile.sso.tootlip.enable'
                    : 'setup_profile.sso.tootlip.disable',
                )}
              >
                <Switch
                  size="small"
                  checked={googleSSO?.enabled || false}
                  onChange={(enable) => {
                    if (googleSSO) {
                      onChange(googleSSO, enable);
                    } else {
                      createSaml({
                        idpType: IdpType.Google,
                      });
                    }
                  }}
                  disabled={isLoading || samlSSO?.enabled}
                />
              </Tooltip>
            </SwitchWrapper>
          )}

          <IdpWrapper>
            <SubTitle $noMargin>
              <FormattedMessage id="settings.security.saml.google.singin" />
            </SubTitle>
            <SetupBody>
              <FormattedMessage id="settings.security.google.singin.body" />
            </SetupBody>
          </IdpWrapper>
        </Wrapper>

        <Wrapper>
          {samlSSO ? (
            <SwitchWrapper>
              <Tooltip
                title={translate(
                  googleSSO?.enabled
                    ? 'setup_profile.sso.tooltip.only.one.sso'
                    : samlSSO?.enabled
                    ? 'setup_profile.sso.tootlip.enable'
                    : 'setup_profile.sso.tootlip.disable',
                )}
              >
                <Switch
                  size="small"
                  checked={samlSSO?.enabled || false}
                  onChange={(enable) => {
                    onChange(samlSSO, enable);
                  }}
                  disabled={isLoading || googleSSO?.enabled}
                />
              </Tooltip>
            </SwitchWrapper>
          ) : (
            <SpaceDiv />
          )}

          <IdpWrapper>
            <SubTitle $noMargin>
              <FormattedMessage id="settings.security.saml.sso.title" />
            </SubTitle>
            <SetupBody>
              {samlSSO ? (
                <StyledLink onClick={() => showMetaPage(samlSSO?.idpId)}>
                  <FormattedMessage id="settings.saml.wiki-how" />
                </StyledLink>
              ) : (
                <>
                  <FormattedMessage id="settings.security.saml.setup" />{' '}
                  <a
                    href="https://help.rocketlane.com/support/solutions/folders/67000578507"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FormattedMessage id="learn_more" />
                  </a>
                </>
              )}
            </SetupBody>
          </IdpWrapper>
          {samlSSO ? (
            <MoreActionsDropdown
              menu={
                <Menu>
                  <Menu.Header>
                    <span>
                      <FormattedMessage id="more_actions" />
                    </span>
                  </Menu.Header>
                  <Menu.ItemEdit
                    icon={<Edit16 />}
                    onClick={() => onEnableEdit(samlSSO.idpId)}
                  />
                  <Menu.Divider />
                  <Menu.ItemDelete
                    icon={<TrashCan16 />}
                    onClick={() => onDelete(samlSSO.idpId)}
                  />
                </Menu>
              }
            />
          ) : (
            <Tooltip
              title={translate('setup_profile.sso.tooltip.only.one.sso')}
            >
              <Button
                disabled={googleSSO?.enabled}
                type="primary"
                onClick={() =>
                  setEditInfo({
                    enableEdit: true,
                  })
                }
              >
                <FormattedMessage id="saml.setup.button" />
              </Button>
            </Tooltip>
          )}
        </Wrapper>
      </ListWrapper>
      <SetupSamlDrawer
        idpId={editInfo.idpId}
        onClose={onDisableEdit}
        visible={editInfo.enableEdit}
        page={editInfo.page}
      />
    </CategoryWrapper>
  );
};
